import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, FormInstance } from 'antd';

interface Option {
  value: number;
  label: string;
}

interface UseCascadeSelectOptions {
  /** Экземпляр формы Ant Design */
  form: FormInstance;
  /** Поле-триггер (например 'department_id') */
  watchField: string;
  /** Поле, которое нужно очищать при смене триггера */
  clearField: string;
  /** Функция загрузки опций по значению триггера */
  fetchOptions: (triggerValue: number) => Promise<{ id: number; name: string }[]>;
}

interface CascadeSelectResult {
  options: Option[];
  loading: boolean;
  /** true когда поле-триггер не выбрано — Select делаем disabled */
  disabled: boolean;
  /**
   * Вызывать при загрузке существующей записи.
   * Загружает опции по triggerValue БЕЗ очистки зависимого поля.
   */
  init: (triggerValue: number | null | undefined) => Promise<void>;
}

/**
 * Хук для каскадных выпадающих списков.
 *
 * Пример:
 *   const positions = useCascadeSelect({
 *     form,
 *     watchField: 'department_id',
 *     clearField: 'position_id',
 *     fetchOptions: (deptId) =>
 *       api.get(`/positions?department_id=${deptId}`).then(r => r.data),
 *   });
 *
 *   <Select
 *     options={positions.options}
 *     loading={positions.loading}
 *     disabled={positions.disabled}
 *   />
 */
export function useCascadeSelect({
  form,
  watchField,
  clearField,
  fetchOptions,
}: UseCascadeSelectOptions): CascadeSelectResult {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  // Form.useWatch — официальный API Ant Design v5:
  // возвращает текущее значение поля и перерисовывает при изменении
  const triggerValue = Form.useWatch(watchField, form);

  // Флаг: идёт ли init-загрузка, чтобы НЕ очищать поле при первом рендере
  const isInitializing = useRef(false);
  const prevTriggerValue = useRef<number | null>(null);

  const load = useCallback(async (value: number) => {
    setLoading(true);
    try {
      const data = await fetchOptions(value);
      setOptions(data.map(item => ({ value: item.id, label: item.name })));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [fetchOptions]);

  // Реагируем на изменение поля-триггера пользователем
  useEffect(() => {
    // Пропускаем, если значение не изменилось или идёт init
    if (isInitializing.current) return;
    if (triggerValue === prevTriggerValue.current) return;

    prevTriggerValue.current = triggerValue ?? null;

    if (triggerValue) {
      // Пользователь выбрал подразделение — очищаем и загружаем
      form.setFieldValue(clearField, null);
      load(triggerValue);
    } else {
      // Пользователь снял подразделение — очищаем оба поля
      form.setFieldValue(clearField, null);
      setOptions([]);
    }
  }, [triggerValue, form, clearField, load]);

  /**
   * Вызывать при загрузке существующего сотрудника.
   * Загружает опции для уже выбранного триггера, НЕ трогая clearField.
   */
  const init = useCallback(async (value: number | null | undefined) => {
    if (!value) return;
    isInitializing.current = true;
    prevTriggerValue.current = value;
    await load(value);
    isInitializing.current = false;
  }, [load]);

  return {
    options,
    loading,
    disabled: !triggerValue,
    init,
  };
}
